import {Box, Checkbox} from '@viptech/react-components'
import CheckboxTable from 'src/components/CheckboxTable'
import LinkWithAction from 'src/components/LinkWithAction'
import RowHover from 'src/components/RowHover/RowHover'
import {DataTablePagination} from 'src/components/data-table-pagination'
import Table from 'src/components/table/Table'
import TableData from 'src/components/table/components/tableData/TableData'
import {TableHeaderColumn, TableHeaderRow} from 'src/components/table/components/tableHeader/TableHeaderBase'
import {AlarmCenter} from 'src/services/api/endpoints/AlarmCenterEndpoint'
import {Page} from 'src/types'
import createStyleSheet from 'src/utilities/createStyleSheet'
import {formatLineModelLabel} from '../alarmCentersHelpers'
import {AlarmCentersTableOption} from './AlarmCentersTableOption'

type Props = {
  alarmCenters: AlarmCenter[]
  clientNameById: Map<number, string>
  totalPages: number
  page: Page
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  isLoading: boolean
  selectedIds: number[]
  setSelectedIds: (ids: number[]) => void
  onEdit: (alarmCenter: AlarmCenter) => void
  onManageZones: (alarmCenter: AlarmCenter) => void
  onDelete: (alarmCenter: AlarmCenter) => void
}

const columns = [
  {id: 'checkbox', name: ''},
  {id: 'hardwareId', name: 'Hardware ID'},
  {id: 'accountNumber', name: 'Conta'},
  {id: 'lineModel', name: 'Modelo'},
  {id: 'client', name: 'Cliente'},
  {id: 'status', name: 'Status'},
  {id: 'options', name: ''},
]

function AlarmCentersGridTable({
  alarmCenters,
  clientNameById,
  totalPages,
  page,
  onPageChange,
  onPageSizeChange,
  isLoading,
  selectedIds,
  setSelectedIds,
  onEdit,
  onManageZones,
  onDelete,
}: Props) {
  const tableControl = {
    pageIndex: page.page,
    pageSize: page.pageSize,
    total: totalPages,
    setPageSize: onPageSizeChange,
    setPageIndex: onPageChange,
    nextPage: () => onPageChange(page.page + 1),
    previousPage: () => onPageChange(page.page - 1),
  }

  const onSelectAll = () => {
    if (selectedIds.length) return setSelectedIds([])
    setSelectedIds(alarmCenters.map((row) => row.id))
  }

  const customHeader = (
    <thead>
      <TableHeaderRow>
        {columns.map((col) =>
          col.id === 'checkbox' ? (
            <TableHeaderColumn key={col.id}>
              <div style={{paddingLeft: '10px'}}>
                <Checkbox id='check-all-alarm-centers' checked={selectedIds.length > 0 && selectedIds.length === alarmCenters.length} onChange={onSelectAll} />
              </div>
            </TableHeaderColumn>
          ) : (
            <TableHeaderColumn key={col.id}>{col.name}</TableHeaderColumn>
          ),
        )}
      </TableHeaderRow>
    </thead>
  )

  return (
    <Box style={styles.gridItem}>
      <Box flex={1} display='flex'>
        <Table columns={columns} skeletonRowsAmount={page.pageSize} isLoading={isLoading} tableHeaderChildren={customHeader}>
          {alarmCenters.map((row) => (
            <RowHover key={row.id} id={`row-${row.id}`}>
              <TableData paddingLeft='10px'>
                <CheckboxTable id={row.id} selectedIds={selectedIds} setSelectedIds={setSelectedIds} />
              </TableData>
              <TableData>
                <LinkWithAction onClick={() => onEdit(row)}>{row.hardwareId}</LinkWithAction>
              </TableData>
              <TableData>{row.accountNumber}</TableData>
              <TableData>{formatLineModelLabel(row.lineModel)}</TableData>
              <TableData>{clientNameById.get(row.clientId) ?? `#${row.clientId}`}</TableData>
              <TableData>{row.isActive ? 'Ativa' : 'Inativa'}</TableData>
              <TableData>
                <AlarmCentersTableOption onEdit={() => onEdit(row)} onManageZones={() => onManageZones(row)} onDelete={() => onDelete(row)} />
              </TableData>
            </RowHover>
          ))}
        </Table>
      </Box>

      {!isLoading && alarmCenters.length === 0 ? <Box style={styles.noRegisters}>Nenhum registro encontrado</Box> : null}

      {!isLoading && alarmCenters.length > 0 ? (
        <Box style={styles.pagination}>
          <DataTablePagination table={tableControl} />
        </Box>
      ) : null}
    </Box>
  )
}

const styles = createStyleSheet({
  gridItem: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '0 20px 20px',
  },
  noRegisters: {
    textAlign: 'center',
    padding: '24px 0',
    color: '#667085',
    fontSize: '14px',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '12px',
  },
})

export default AlarmCentersGridTable
